import { logger } from '../utils/logger';
import { Workplace, Expert, Doctor, Dsp } from '../types/entities';
// Updated API service with authentication support and generic CRUD
// Use environment variable or proxy for mobile compatibility
// In development: uses proxy defined in package.json (http://localhost:5005)
// In production: uses REACT_APP_API_URL environment variable
// Use environment variable or dynamic detection for production
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    const url = process.env.REACT_APP_API_URL.replace(/\/$/, '');
    return url.endsWith('/api') ? url : `${url}/api`;
  }
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();
import { io } from 'socket.io-client';


// Store token in memory (in a real app, you might want to use localStorage)
let authToken: string | null = null;
let organizationId: number | null = null;
// Active workplace ID for client portal
let activeWorkplaceId: number | null = (() => {
  const stored = localStorage.getItem('activeWorkplaceId');
  return stored ? parseInt(stored) : null;
})();

// Helper function to get organizationId from JWT token
const getOrganizationIdFromToken = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.organizationId || null;
  } catch (error) {
    logger.error('Error parsing token for organizationId:', error);
    return null;
  }
};

// Initialize auth token and organization ID from localStorage
if (typeof window !== 'undefined') {
  authToken = localStorage.getItem('authToken');
  // Always get organizationId from token (most secure and accurate)
  organizationId = getOrganizationIdFromToken(authToken);

  // If token doesn't have organizationId but localStorage does, sync it
  // This handles edge cases but token should always be the source of truth
  if (!organizationId) {
    const storedOrgId = localStorage.getItem('organizationId');
    if (storedOrgId) {
      organizationId = parseInt(storedOrgId, 10);
    }
  } else {
    // Sync localStorage with token's organizationId
    localStorage.setItem('organizationId', organizationId.toString());
  }

  // Initialize active workplace ID from localStorage
  const storedWorkplaceId = localStorage.getItem('activeWorkplaceId');
  if (storedWorkplaceId) {
    activeWorkplaceId = parseInt(storedWorkplaceId, 10);
  }
}

// Rate limiting
import { RATE_LIMIT, CACHE } from '../constants';
const requestTimestamps: number[] = [];

const checkRateLimit = () => {
  const now = Date.now();
  while (requestTimestamps.length > 0 && requestTimestamps[0] < now - RATE_LIMIT.WINDOW) {
    requestTimestamps.shift();
  }
  if (requestTimestamps.length >= RATE_LIMIT.MAX_REQUESTS) {
    throw new Error('Rate limit exceeded. Please try again later.');
  }
  requestTimestamps.push(now);
};

// Input sanitization
const sanitizeInput = (input: string): string => {
  return input.replace(/<[^>]*>/g, '');
};

const sanitizeObject = (obj: any): any => {
  if (typeof obj === 'string') return sanitizeInput(obj);
  if (Array.isArray(obj)) return obj.map(sanitizeObject);
  if (obj && typeof obj === 'object') {
    const sanitized: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) sanitized[key] = sanitizeObject(obj[key]);
    }
    return sanitized;
  }
  return obj;
};

// Simple in-memory cache
const apiCache = new Map<string, { data: any; timestamp: number }>();

const getCachedData = (key: string) => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE.TTL) {
    console.log('📦 getCachedData: CACHE HIT for', key);
    return cached.data;
  }
  console.log('📭 getCachedData: CACHE MISS for', key, cached ? '(expired)' : '(not found)');
  apiCache.delete(key);
  return null;
};

const setCachedData = (key: string, data: any) => {
  apiCache.set(key, { data, timestamp: Date.now() });
};

export const clearCache = (pattern: string) => {
  const keys = Array.from(apiCache.keys());
  const matchingKeys = keys.filter(key => key.includes(pattern));
  console.log('🧹 clearCache called:', { pattern, matchingKeysCount: matchingKeys.length, matchingKeys });
  matchingKeys.forEach(key => apiCache.delete(key));
};

// Setup Socket.IO to listen for real-time cache invalidation
// This ensures mobile updates are immediately reflected on web
export let socket: any = null;

if (typeof window !== 'undefined') {
  try {
    // Connect directly to backend server (not via proxy, as WebSocket proxy doesn't work in dev)
    // In production, this will be the same origin as the API
    const socketUrl = process.env.NODE_ENV === 'production'
      ? window.location.origin
      : 'http://localhost:5005';

    socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true
    });

    // Listen for workplace updates from backend
    socket.on('workplaceUpdate', (data: any) => {
      logger.debug('[api.ts] Socket: workplaceUpdate received, clearing frontend cache', data);
      clearCache('workplaces');
      clearCache('experts');
      clearCache('doctors');
      clearCache('dsps');
      clearCache('welcome_page_stats');
      clearCache('general_statistics');
      clearCache('dashboard_summary');
      clearCache('dashboard_trend_');
    });

    // Listen for personnel updates from backend (experts/doctors)
    socket.on('personnelUpdate', (data: any) => {
      logger.debug('[api.ts] Socket: personnelUpdate received, clearing frontend cache', data);
      clearCache('experts');
      clearCache('doctors');
      clearCache('welcome_page_stats');
      clearCache('general_statistics');
      clearCache('dashboard_summary');
      clearCache('dashboard_trend_');
      clearCache('experts_visit_stats');
      clearCache('doctors_visit_stats');
    });
    // Listen for visit updates from backend
    socket.on('visitUpdate', (data: any) => {
      logger.debug('[api.ts] Socket: visitUpdate received, clearing frontend cache', data);
      clearCache('workplaces');
      clearCache('visits');
      clearCache('experts_visit_stats');
      clearCache('doctors_visit_stats');
    });

    logger.debug('[api.ts] Socket.IO cache invalidation listener initialized');

    // Notify initial load (optional)
    window.dispatchEvent(new Event('personnelUpdated'));

  } catch (error) {
    logger.error('[api.ts] Socket initialization error:', error);
  }
}


// Organization Management (Super Admin)
export const getAllOrganizations = async () => {
  return await apiRequest('/organizations', 'GET');
};

export const updateOrganization = async (id: number, data: any) => {
  return await apiRequest(`/organizations/${id}`, 'PUT', data);
};

export const deleteOrganization = async (id: number) => {
  return await apiRequest(`/organizations/${id}`, 'DELETE');
};

export const getOrganizationStats = async (id: number) => {
  return await apiRequest(`/organizations/${id}/stats`, 'GET');
};

// Create headers
const getHeaders = (skipContentType: boolean = false) => {
  const headers: Record<string, string> = {};
  if (!skipContentType) {
    headers['Content-Type'] = 'application/json';
  }

  // Rate limiting check - temporarily disabled for pages with many requests
  // ... (keeping comments)

  // Determine available token (check both main and field/mobile tokens)
  let effectiveToken = authToken;
  
  if (typeof window !== 'undefined') {
    const isFieldApp = window.location.pathname.includes('/field') || window.location.pathname.includes('/mobile');
    
    if (isFieldApp) {
      // Eğer saha uygulamasındaysak (web/field) sadece fieldToken kullanılmalı, ana admin authToken'u YOK sayılmalı!
      effectiveToken = localStorage.getItem('fieldToken');
    } else if (!effectiveToken) {
      // Geriye dönük uyumluluk veya yedek senaryo için
      effectiveToken = localStorage.getItem('fieldToken');
    }
  }

  if (effectiveToken) {
    headers['Authorization'] = `Bearer ${effectiveToken}`;
    // Get organizationId from token (always fresh, not from localStorage)
    const tokenOrgId = getOrganizationIdFromToken(effectiveToken);
    if (tokenOrgId && !isNaN(tokenOrgId)) {
      headers['x-organization-id'] = tokenOrgId.toString();
    }
  }

  // Add active workplace ID for client users
  if (activeWorkplaceId) {
    headers['x-workplace-id'] = activeWorkplaceId.toString();
  }

  return headers;
};

// Generic API Helper
export const apiRequest = async (endpoint: string, method: string, body?: any, requestOptions?: { skipBranchFilter?: boolean }) => {
  const options: RequestInit = {
    method,
    headers: getHeaders(),
    cache: 'no-store', // Prevent browser caching
  };
  if (body) {
    const sanitizedBody = sanitizeObject(body);
    // DEBUG: Log payload for monthlyVisits debugging
    if (endpoint.includes('/workplaces/') && method === 'PUT') {
      if (sanitizedBody.monthlyVisits) {
        // Check if notes are present in monthlyVisits
        let notesCount = 0;
        const monthlyVisits = sanitizedBody.monthlyVisits;
        if (monthlyVisits && typeof monthlyVisits === 'object') {
          Object.keys(monthlyVisits).forEach(year => {
            if (monthlyVisits[year] && typeof monthlyVisits[year] === 'object') {
              Object.keys(monthlyVisits[year]).forEach(month => {
                const monthData = monthlyVisits[year][month];
                if (monthData && typeof monthData === 'object' && monthData.note) {
                  notesCount++;
                }
              });
            }
          });
        }

      }
    }
    options.body = JSON.stringify(sanitizedBody);
  }

  // Add branch filter if applicable to all non-auth requests
  let finalEndpoint = endpoint;
  if (!endpoint.includes('/auth/') && !requestOptions?.skipBranchFilter) {
    const selectedBranch = localStorage.getItem('selectedBranch');
    if (selectedBranch && selectedBranch !== 'Tüm Şubeler') {
      const separator = finalEndpoint.includes('?') ? '&' : '?';
      finalEndpoint += `${separator}branchCity=${encodeURIComponent(selectedBranch)}`;
    }
  }

  const response = await fetch(`${API_BASE_URL}${finalEndpoint}`, options);

  if (!response.ok) {
    let errorData: any = {};
    try {
      const text = await response.text();
      errorData = text ? JSON.parse(text) : {};
    } catch (e) {
      // Ignore JSON parse errors
    }

    let errorMessage = errorData.message || `API Request Failed (${response.status}: ${response.statusText})`;

    // If there are validation errors, append them to the message
    // Check both errors and details (details is used in backend for personnel validation)
    const validationErrors = errorData.errors || errorData.details || [];
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      // Handle both formats: {msg: string} and string
      const validationMessages = validationErrors.map((err: any) =>
        typeof err === 'string' ? err : (err.msg || err.message || err)
      ).join(', ');
      errorMessage += `: ${validationMessages}`;
    }

    // [GLOBAL INTERCEPTOR]: Sistemin kırık sayfalar göstermesini OLUŞMADAN önler!
    if (response.status === 403 && errorData.code === 'SUBSCRIPTION_EXPIRED') {
      logger.warn('Subscription expired intercepted at apiRequest.', errorData);
      if (typeof window !== 'undefined') {
        const isFieldApp = window.location.pathname.includes('/field') || window.location.pathname.includes('/mobile');
        
        // Saha personeli için uyarı çıkart ve logine at, web admin için direk abonelik sayfasına at
        if (isFieldApp) {
          alert('Bağlı bulunduğunuz organizasyonun abonelik süresi dolmuştur. İşleme devam edilemez.');
          localStorage.removeItem('fieldToken');
          localStorage.removeItem('fieldUser');
          window.location.href = '/web/field/login';
        } else {
          window.location.href = errorData.redirectUrl || '/subscription';
        }
      }
    }

    logger.error('apiRequest: Full error data:', errorData);
    throw new Error(errorMessage);
  }

  const responseText = await response.text();
  if (!responseText || responseText.trim() === '') {
    return {};
  }

  try {
    return JSON.parse(responseText);
  } catch (parseError) {
    logger.error('API Response JSON Parse Error:', {
      error: parseError,
      url: `${API_BASE_URL}${endpoint}`,
      text: responseText.substring(0, 100) + '...'
    });
    // If it's not valid JSON, return as is (could be success message or empty)
    return { message: responseText };
  }
};

// Generic CRUD Service Factory
const createCrudService = (resource: string) => ({
  getAll: async () => {
    const selectedBranch = localStorage.getItem('selectedBranch') || 'Tüm Şubeler';
    const cacheKey = `${resource}_${selectedBranch}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;
    const data = await apiRequest(`/${resource}`, 'GET');
    setCachedData(cacheKey, data);
    return data;
  },
  getById: async (id: number) => apiRequest(`/${resource}/${id}`, 'GET', undefined, { skipBranchFilter: true }),
  create: async (data: any) => {
    const result = await apiRequest(`/${resource}`, 'POST', data);
    clearCache(resource);
    return result;
  },
  update: async (id: number, data: any) => {
    const result = await apiRequest(`/${resource}/${id}`, 'PUT', data);
    clearCache(resource);
    return result;
  },
  delete: async (id: number) => {
    const result = await apiRequest(`/${resource}/${id}`, 'DELETE');
    clearCache(resource);
    return result;
  }
});

// Auth Functions
export const login = async (username: string, password: string, organizationName: string) => {
  const data = await apiRequest('/auth/login', 'POST', { username, password, organizationName });
  if (data.token) {
    setAuthToken(data.token); // This will automatically extract organizationId from token
  }
  return data;
};

export const verify2FALogin = async (tempToken: string, token: string) => {
  const data = await apiRequest('/auth/2fa/login', 'POST', { tempToken, token });
  if (data.token) {
    setAuthToken(data.token);
  }
  return data;
};

export const setup2FA = async () => {
  return await apiRequest('/auth/2fa/setup', 'POST');
};

export const verify2FA = async (token: string) => {
  return await apiRequest('/auth/2fa/verify', 'POST', { token });
};

export const disable2FA = async () => {
  return await apiRequest('/auth/2fa/disable', 'POST');
};

export const registerOrganization = async (orgData: any) => {
  const data = await apiRequest('/auth/register-organization', 'POST', orgData);
  if (data.token) {
    setAuthToken(data.token); // This will automatically extract organizationId from token
  }
  return data;
};

export const getCurrentUser = () => apiRequest('/auth/me', 'GET');

export const logout = () => {
  setAuthToken('');
  setOrganizationId(0);
  setActiveWorkplaceId(null);
  localStorage.removeItem('authToken');
  localStorage.removeItem('organizationId');
};

// Token Management
export const setAuthToken = (token: string) => {
  const oldOrgId = organizationId;
  authToken = token;
  if (token) {
    localStorage.setItem('authToken', token);
    // Update organizationId from token when token is set (always fresh from JWT)
    const tokenOrgId = getOrganizationIdFromToken(token);
    if (tokenOrgId) {
      organizationId = tokenOrgId;
      localStorage.setItem('organizationId', tokenOrgId.toString());

      // Clear cache if organizationId changed (user switched organizations)
      if (oldOrgId && oldOrgId !== tokenOrgId) {
        clearCache('experts');
        clearCache('doctors');
        clearCache('dsps');
        clearCache('workplaces');
        clearCache('visits');
      }
    } else {
      // If token doesn't have organizationId, clear it
      organizationId = null;
      localStorage.removeItem('organizationId');
      // Clear all cache when invalid token
      clearCache('experts');
      clearCache('doctors');
      clearCache('dsps');
      clearCache('workplaces');
      clearCache('visits');
    }
  } else {
    localStorage.removeItem('authToken');
    localStorage.removeItem('organizationId');
    organizationId = null;
    // Clear all cache when logging out
    clearCache('experts');
    clearCache('doctors');
    clearCache('dsps');
    clearCache('workplaces');
    clearCache('visits');
  }
};

export const setOrganizationId = (orgId: number) => {
  organizationId = orgId;
  orgId ? localStorage.setItem('organizationId', orgId.toString()) : localStorage.removeItem('organizationId');
};

export const setActiveWorkplaceId = (workplaceId: number | null) => {
  activeWorkplaceId = workplaceId;
  if (workplaceId) {
    localStorage.setItem('activeWorkplaceId', workplaceId.toString());
  } else {
    localStorage.removeItem('activeWorkplaceId');
  }
  // Clear statistics and dashboard caches when switching workplaces
  clearCache('dashboard');
  clearCache('statistics');
  clearCache('activities');
  clearCache('documents');
  clearCache('employees');

  // Dispatch event so other components know the active workplace changed
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('workplaceChanged'));
  }
};

export const getActiveWorkplaceId = () => activeWorkplaceId;

// Specific Services using Generic CRUD
const expertsBase = createCrudService('experts');
export const experts = {
  ...expertsBase,
  update: async (id: number, data: any) => {
    const result = await expertsBase.update(id, data);
    clearCache('welcome_page_stats');
    clearCache('general_statistics');
    clearCache('dashboard_summary');
    clearCache('dashboard_trend_');
    clearCache('experts_visit_stats');
    return result;
  },
  getAssignedWorkplaces: (id: number) => apiRequest(`/workplaces/expert/${id}`, 'GET'),
  getAssignmentHistory: (id: number) => apiRequest(`/experts/${id}/assignment-history`, 'GET'),
};

const doctorsBase = createCrudService('doctors');
export const doctors = {
  ...doctorsBase,
  update: async (id: number, data: any) => {
    const result = await doctorsBase.update(id, data);
    clearCache('welcome_page_stats');
    clearCache('general_statistics');
    clearCache('dashboard_summary');
    clearCache('dashboard_trend_');
    clearCache('doctors_visit_stats');
    return result;
  },
  getAssignedWorkplaces: (id: number) => apiRequest(`/workplaces/doctor/${id}`, 'GET'),
  getAssignmentHistory: (id: number) => apiRequest(`/doctors/${id}/assignment-history`, 'GET'),
};

export const dsps = {
  ...createCrudService('dsps'),
  getAssignmentHistory: (id: number) => apiRequest(`/dsps/${id}/assignment-history`, 'GET'),
};

// Workplaces (Specialized)
export const getWorkplacesPaginated = async (page: number = 1, limit: number = 10, sort?: string, search?: string, excludeArchived?: boolean, personnelSearch?: string): Promise<any> => {
  let query = `?page=${page}&limit=${limit}`;
  if (sort) query += `&sort=${encodeURIComponent(sort)}`;
  if (search) query += `&search=${encodeURIComponent(search)}`;
  if (personnelSearch) query += `&personnelSearch=${encodeURIComponent(personnelSearch)}`;
  if (excludeArchived !== undefined) query += `&excludeArchived=${excludeArchived}`;
  return await apiRequest(`/workplaces${query}`, 'GET');
};

export const bulkUpdateWorkplaceStatuses = async (ids: number[], status: string) => {
  const response = await apiRequest('/workplaces/bulk-status', 'POST', { ids, status });

  // Clear related caches if any frontend logic relies on them
  clearCache('workplaces');
  clearCache('experts_visit_stats');
  clearCache('general_statistics');
  clearCache('dashboard_summary');
  // Dispatch event so other components know data changed
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('workplaceUpdated'));
  }

  return response?.data || response;
};

const workplacesBase = createCrudService('workplaces');
export const workplaces = {
  ...workplacesBase,
  getAll: async (ignoreBranch: boolean = false) => {
    const selectedBranch = localStorage.getItem('selectedBranch') || 'Tüm Şubeler';
    const cacheKey = `workplaces_${ignoreBranch ? 'all_branches' : selectedBranch}_${ignoreBranch}`;
    const cached = getCachedData(cacheKey);
    if (cached) return cached;
    const endpoint = ignoreBranch ? '/workplaces?ignoreBranch=true' : '/workplaces';
    const data = await apiRequest(endpoint, 'GET', undefined, ignoreBranch ? { skipBranchFilter: true } : undefined);
    setCachedData(cacheKey, data);
    return data;
  },
  create: async (data: any) => {
    const result = await workplacesBase.create(data);
    clearCache('experts');
    clearCache('doctors');
    clearCache('dsps');
    clearCache('experts_visit_stats'); // Clear visit stats cache
    clearCache('workplaces'); // Clear workplaces cache
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('workplaceCreated'));
    }
    return result;
  },
  update: async (id: number, data: any) => {
    const result = await workplacesBase.update(id, data);
    clearCache('experts');
    clearCache('doctors');
    clearCache('dsps');
    clearCache('experts_visit_stats'); // Clear visit stats cache
    clearCache('workplaces'); // Clear workplaces cache
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('workplaceUpdated'));
    }
    return result;
  },
  delete: async (id: number) => {
    const result = await workplacesBase.delete(id);
    clearCache('experts');
    clearCache('doctors');
    clearCache('dsps');
    clearCache('experts_visit_stats'); // Clear visit stats cache
    clearCache('workplaces'); // Clear workplaces cache
    clearCache('welcome_page_stats');
    clearCache('general_statistics');
    clearCache('dashboard_summary');
    clearCache('dashboard_trend_');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('workplaceDeleted'));
    }
    return result;
  },
  dropPersonnel: async (id: number, personnelType: 'expert' | 'doctor' | 'dsp') => {
    const result = await apiRequest(`/workplaces/${id}/drop-personnel`, 'PUT', { personnelType });
    clearCache('dsps');
    return result;
  },
  getWithoutAccounts: async (query?: string) => {
    const url = query ? `/workplaces/without-accounts?query=${encodeURIComponent(query)}` : '/workplaces/without-accounts';
    return await apiRequest(url, 'GET');
  },
  createClientAccount: async (workplaceId: number | number[]) => {
    if (Array.isArray(workplaceId)) {
      const result = await apiRequest(`/workplaces/bulk/client-account`, 'POST', { workplaceIds: workplaceId });
      clearCache('workplaces');
      return result;
    } else {
      const result = await apiRequest(`/workplaces/${workplaceId}/client-account`, 'POST');
      clearCache('workplaces');
      return result;
    }
  },
  /** Var olan işveren kullanıcısına seçili sicilleri bağlar (aynı kullanıcı adı / şifre ile seçmeli sicil listesi). */
  appendClientWorkplaces: async (clientUserId: number, workplaceIds: number[]) => {
    const result = await apiRequest(`/workplaces/bulk/append-client-workplaces`, 'POST', {
      clientUserId,
      workplaceIds
    });
    clearCache('workplaces');
    return result;
  }
};
export const visits = {
  ...createCrudService('visits'),
  create: async (data: any) => {
    const result = await apiRequest('/visits', 'POST', data);
    clearCache('visits');
    clearCache('experts_visit_stats');
    clearCache('doctors_visit_stats');
    return result;
  },
  delete: async (id: number) => {
    const result = await apiRequest(`/visits/${id}`, 'DELETE');
    clearCache('visits');
    clearCache('experts_visit_stats');
    clearCache('doctors_visit_stats');
    return result;
  }
};

// Tüm şubelerdeki personeli getir (işyeri atama dropdown'ları için — branchCity filtresi yok)
export const getAllExpertsAllBranches = () => apiRequest('/experts', 'GET', undefined, { skipBranchFilter: true });
export const getAllDoctorsAllBranches = () => apiRequest('/doctors', 'GET', undefined, { skipBranchFilter: true });
export const getAllDspsAllBranches    = () => apiRequest('/dsps',    'GET', undefined, { skipBranchFilter: true });
export const getPersonnelManagementData = () => apiRequest('/accounting/personnel-management-data', 'GET');

// Legacy exports for backward compatibility (can be deprecated later)
export const getAllExperts = experts.getAll;
export const getExpertById = experts.getById;
export const createExpert = experts.create;
export const updateExpert = experts.update;
export const deleteExpert = experts.delete;
export const getExpertAssignedWorkplaces = experts.getAssignedWorkplaces;

export const getAllDoctors = doctors.getAll;
export const getDoctorById = doctors.getById;
export const createDoctor = doctors.create;
export const updateDoctor = doctors.update;
export const deleteDoctor = doctors.delete;
export const getDoctorAssignedWorkplaces = doctors.getAssignedWorkplaces;

// Legacy exports for workplaces
export const getAllWorkplaces = workplaces.getAll;
export const getWorkplaceById = workplaces.getById;
export const createWorkplace = workplaces.create;
export const updateWorkplace = workplaces.update;

// Workplace Documents
export const uploadWorkplaceDocument = async (workplaceId: number, documentType: string, file: File, documentDate?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);
  if (documentDate) formData.append('documentDate', documentDate);

  const response = await fetch(`${API_BASE_URL}/workplaces/${workplaceId}/documents`, {
    method: 'POST',
    headers: getHeaders(true), // Don't set Content-Type for FormData
    body: formData
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData = { message: 'Upload failed' };
    try { errorData = JSON.parse(errorText); } catch (e) { }
    throw new Error(errorData.message || 'Document upload failed');
  }

  const responseText = await response.text();
  try { return JSON.parse(responseText); } catch (e) { return { message: responseText }; }
};

export const getWorkplaceDocuments = async (workplaceId: number) => {
  return apiRequest(`/workplaces/${workplaceId}/documents`, 'GET');
};

export const getWorkplaceDocument = async (workplaceId: number, documentId: number) => {
  const response = await fetch(`${API_BASE_URL}/workplaces/${workplaceId}/documents/${documentId}`, {
    method: 'GET',
    headers: getHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to fetch document');
  }

  // Return blob URL for viewing
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const deleteWorkplaceDocument = async (workplaceId: number, documentId: number) => {
  return apiRequest(`/workplaces/${workplaceId}/documents/${documentId}`, 'DELETE');
};

export const getPendingApprovals = async (branchCity?: string) => {
  const query = branchCity && branchCity !== 'all' && branchCity !== 'Tüm Şubeler' ? `?branchCity=${encodeURIComponent(branchCity)}` : '';
  return apiRequest(`/workplaces/documents/pending-approvals${query}`, 'GET', undefined, { skipBranchFilter: true });
};

export const approveDocument = async (documentId: number) => {
  return apiRequest(`/workplaces/documents/${documentId}/approve`, 'POST', undefined, { skipBranchFilter: true });
};

export const bulkApproveDocuments = async (documentIds: number[]) => {
  return apiRequest('/workplaces/documents/bulk-approve', 'POST', { documentIds }, { skipBranchFilter: true });
};

export const rejectDocument = async (documentId: number, reason?: string) => {
  return apiRequest(`/workplaces/documents/${documentId}/reject`, 'POST', { reason }, { skipBranchFilter: true });
};

export const deleteWorkplace = workplaces.delete;


export const getAllDsps = dsps.getAll;
export const getDspById = dsps.getById;
export const createDsp = dsps.create;
export const updateDsp = dsps.update;
export const deleteDsp = dsps.delete;



export const getAllVisits = visits.getAll;
export const createVisit = visits.create;
export const updateVisit = visits.update;
export const deleteVisit = visits.delete;

// System Config (Custom)
export const getAllSystemConfig = async () => {
  const cacheKey = 'system_config';
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const data = await apiRequest('/system-config', 'GET');
  setCachedData(cacheKey, data);
  return data;
};

export const getSystemConfigByKey = async (key: string) => {
  const cacheKey = `system_config_${key}`;
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const data = await apiRequest(`/system-config/${key}`, 'GET');
  setCachedData(cacheKey, data);
  return data;
};

export const upsertSystemConfig = async (config: any) => {
  const data = await apiRequest('/system-config', 'POST', config);
  clearCache('system_config');
  return data;
};

export const deleteSystemConfig = async (key: string) => {
  const data = await apiRequest(`/system-config/${key}`, 'DELETE');
  clearCache('system_config');
  return data;
};

// DSP specific
export const getDspAssignedWorkplaces = async (dspId: number) => {
  return await apiRequest(`/workplaces/dsp/${dspId}`, 'GET');
};

// Visits specific
export const getVisitsForExpert = async (expertId: number) => {
  return await apiRequest(`/visits/expert/${expertId}`, 'GET');
};

export const getVisitsForDoctor = async (doctorId: number) => {
  return await apiRequest(`/visits/doctor/${doctorId}`, 'GET');
};

export const getVisitsByWorkplace = async (workplaceId: number) => {
  return await apiRequest(`/visits/workplace/${workplaceId}`, 'GET');
};

// Expert visit stats (optimized batch endpoint)
export const getAllExpertsWithVisitStats = async (year?: number, month?: number) => {
  let query = '';
  if (year) query += `year=${year}`;
  if (month) query += (query ? '&' : '') + `month=${month}`;

  const queryString = query ? `?${query}` : '';
  const data = await apiRequest(`/experts/visit-stats${queryString}`, 'GET');
  return data;
};

export const getAllDoctorsWithVisitStats = async (year?: number, month?: number) => {
  let query = '';
  if (year) query += `year=${year}`;
  if (month) query += (query ? '&' : '') + `month=${month}`;

  const queryString = query ? `?${query}` : '';
  try {
    const data = await apiRequest(`/doctors/visit-stats${queryString}`, 'GET');

    // Check if response is an error object
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      if (data.success === false || data.message || data.statusCode) {
        logger.error('getAllDoctorsWithVisitStats: API returned error object:', data);
        throw new Error(data.message || 'API returned error response');
      }
    }

    // Ensure we return an array
    if (Array.isArray(data)) {
      return data;
    } else {
      logger.error('getAllDoctorsWithVisitStats: API returned non-array, returning empty array. Response:', data);
      return [];
    }
  } catch (error: any) {
    logger.error('getAllDoctorsWithVisitStats: Error fetching data:', error);
    // Re-throw to be handled by caller
    throw error;
  }
};

// User profile
export const updateUserProfile = async (userId: number, profileData: any) => {
  return await apiRequest(`/auth/me`, 'PUT', profileData);
};

// Register (alias for registerOrganization for backward compatibility)
export const register = registerOrganization;

// Statistics
export const getGeneralStatistics = async () => {
  const selectedBranch = localStorage.getItem('selectedBranch') || 'Tüm Şubeler';
  const cacheKey = `general_statistics_${selectedBranch}`;
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const response = await apiRequest('/statistics/general', 'GET');
  // Backend returns { success: true, data: statistics }
  const data = response?.data || response;
  setCachedData(cacheKey, data);
  return data;
};

export const getCoordinatorAuditStats = async (filters: any = {}) => {
  const queryParams = new URLSearchParams();
  if (filters.coordinatorId) queryParams.append('coordinatorId', String(filters.coordinatorId));
  if (filters.branch) queryParams.append('branch', filters.branch);
  if (filters.timeRange) queryParams.append('timeRange', filters.timeRange);
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);
  if (filters.documentType) queryParams.append('documentType', filters.documentType);

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  const response = await apiRequest(`/statistics/coordinator-audit${queryString}`, 'GET');
  return response?.data || response;
};

export const getFilteredWorkplaces = async (filters: any) => {
  const selectedBranch = localStorage.getItem('selectedBranch') || 'Tüm Şubeler';
  const branchUpdate = selectedBranch !== 'Tüm Şubeler' ? { branchCity: selectedBranch } : {};
  
  // Don't cache filtered results as they change frequently
  const response = await apiRequest('/statistics/filtered-workplaces', 'POST', {
    ...filters,
    ...branchUpdate
  });
  // Backend returns { success: true, data: workplaces }
  return response?.data || response;
};

export const getDashboardSummary = async () => {
  const selectedBranch = localStorage.getItem('selectedBranch') || 'Tüm Şubeler';
  const cacheKey = `dashboard_summary_${selectedBranch}`;
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const response = await apiRequest('/statistics/dashboard/summary', 'GET');
  const data = response?.data || response;
  setCachedData(cacheKey, data);
  return data;
};

export const getDashboardTrend = async (type: 'revenue' | 'expertise' | 'health' | 'employees' | 'success' | 'commercial') => {
  const selectedBranch = localStorage.getItem('selectedBranch') || 'Tüm Şubeler';
  const cacheKey = `dashboard_trend_${type}_${selectedBranch}`;
  const cached = getCachedData(cacheKey);
  if (cached) return cached;
  const response = await apiRequest(`/statistics/dashboard/trends/${type}`, 'GET');
  const data = response?.data || response;
  setCachedData(cacheKey, data);
  return data;
};

export const getWelcomePageStats = async () => {
  const selectedBranch = localStorage.getItem('selectedBranch') || 'Tüm Şubeler';
  const cacheKey = `welcome_page_stats_${selectedBranch}`;
  const response = await apiRequest('/statistics/welcome', 'GET');
  const data = response?.data || response;
  setCachedData(cacheKey, data);
  return data;
};

export const getNotifications = async () => {
  const response = await apiRequest('/statistics/notifications', 'GET');
  // Backend returns { success: true, data: notifications }
  return response?.data || response;
};

export const completeNotification = async (notificationKey: string) => {
  const response = await apiRequest('/statistics/notifications/complete', 'POST', { notificationKey });
  return response?.data || response;
};

export const completeAllNotifications = async (notificationKeys: string[]) => {
  const response = await apiRequest('/statistics/notifications/complete-all', 'POST', { notificationKeys });
  return response?.data || response;
};

// ISG Katip
export const getIsgKatipSummary = async () => {
  const response = await apiRequest('/isg-katip/summary-stats', 'GET');
  return response?.data || response;
};

export const getEmployeeHistory = async (workplaceId: number) => {
  const response = await apiRequest(`/isg-katip/employee-history/${workplaceId}`, 'GET');
  return response?.data || response;
};

export const exportAccountingExcel = async () => {
  try {
    const token = localStorage.getItem('authToken');
    const headers: any = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const orgId = localStorage.getItem('organizationId');
    if (orgId) headers['x-organization-id'] = orgId;

    let url = `${API_BASE_URL}/isg-katip/export-accounting`;
    const selectedBranch = localStorage.getItem('selectedBranch');
    if (selectedBranch && selectedBranch !== 'Tüm Şubeler') {
      url += `?branchCity=${encodeURIComponent(selectedBranch)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    if (!response.ok) throw new Error('Download failed');
    return await response.blob();
  } catch (error) {
    console.error('Error exporting accounting excel:', error);
    throw error;
  }
};

export const exportAccountingExcelFromFile = async (file: File) => {
  try {
    const formData = new FormData();
    formData.append('files', file);

    const token = localStorage.getItem('authToken');
    const headers: any = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const orgId = localStorage.getItem('organizationId');
    if (orgId) headers['x-organization-id'] = orgId;

    let url = `${API_BASE_URL}/isg-katip/export-accounting-upload`;
    const selectedBranch = localStorage.getItem('selectedBranch');
    if (selectedBranch && selectedBranch !== 'Tüm Şubeler') {
      url += `?branchCity=${encodeURIComponent(selectedBranch)}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Download from file failed');
    }
    return await response.blob();
  } catch (error) {
    console.error('Error exporting accounting excel from file:', error);
    throw error;
  }
};

export const generateCertificates = async (excelFile: File) => {
  try {
    const formData = new FormData();
    formData.append('excel_data', excelFile);

    const headers = getHeaders(true);
    const response = await fetch(`${API_BASE_URL}/certificates/generate`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const text = await response.text();
      let message = 'Sertifika oluşturulamadı.';
      try { message = JSON.parse(text).message || message; } catch (e) {}
      throw new Error(message);
    }

    return await response.blob();
  } catch (error) {
    console.error('Error generating certificates:', error);
    throw error;
  }
};

export const uploadCertificateTemplate = async (templateFile: File) => {
  try {
    const formData = new FormData();
    formData.append('template', templateFile);

    const headers = getHeaders(true);
    const response = await fetch(`${API_BASE_URL}/certificates/template`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const text = await response.text();
      let message = 'Şablon yüklenemedi.';
      try { message = JSON.parse(text).message || message; } catch (e) {}
      throw new Error(message);
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading certificate template:', error);
    throw error;
  }
};

export const downloadCertificateExcelTemplate = async () => {
  try {
    const headers = getHeaders();
    const response = await fetch(`${API_BASE_URL}/certificates/excel-template`, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Şablon indirilemedi');
    return await response.blob();
  } catch (error) {
    console.error('Error downloading template:', error);
    throw error;
  }
};

export const downloadCertificateDocxTemplate = async () => {
  try {
    const headers = getHeaders();
    const response = await fetch(`${API_BASE_URL}/certificates/template`, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Word şablonu indirilemedi');
    return await response.blob();
  } catch (error) {
    console.error('Error downloading docx template:', error);
    throw error;
  }
};

export const downloadWorkplaceBulkTemplate = async (branchCity?: string): Promise<Blob> => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const orgId = localStorage.getItem('organizationId');
  if (orgId) headers['x-organization-id'] = orgId;

  let url = `${API_BASE_URL}/workplaces/export-bulk-template`;
  if (branchCity && branchCity !== 'Tüm Şubeler') {
    url += `?branchCity=${encodeURIComponent(branchCity)}`;
  }

  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) throw new Error('Şablon indirme başarısız');
  return await response.blob();
};

export const importWorkplaceBulkUpdate = async (
  file: File,
  branchCity: string | null,
  isPreview: boolean
): Promise<any> => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const orgId = localStorage.getItem('organizationId');
  if (orgId) headers['x-organization-id'] = orgId;

  const formData = new FormData();
  formData.append('file', file);

  let url = `${API_BASE_URL}/workplaces/import-bulk-update?preview=${isPreview}`;
  if (branchCity && branchCity !== 'Tüm Şubeler') {
    url += `&branchCity=${encodeURIComponent(branchCity)}`;
  }

  const response = await fetch(url, { method: 'POST', headers, body: formData });
  if (!response.ok) {
    const text = await response.text();
    let msg = 'Yükleme başarısız';
    try { msg = JSON.parse(text)?.error || msg; } catch (e) { /* ignore */ }
    throw new Error(msg);
  }
  return await response.json();
};

export const getAllEmployeeHistories = async () => {
  const response = await apiRequest('/isg-katip/employee-history', 'GET');
  // Backend returns array directly or inside data? Based on controller it returns array directly or stats object
  // IsgKatipService.getAllEmployeeHistories returns Array.
  return response?.data || response;
};

export const updateEmployeeCounts = async (changes: any[]) => {
  clearCache('workplaces');
  clearCache('welcome_page_stats');
  const response = await apiRequest('/isg-katip/update-employee-counts', 'POST', { changes });
  return response?.data || response;
};

export const checkMinuteAvailability = async (changes: any[]) => {
  const response = await apiRequest('/isg-katip/check-minutes', 'POST', { changes });
  return response?.data || response;
};

export const syncFromBrowser = async (data: any) => {
  const response = await apiRequest('/isg-katip/sync-from-browser', 'POST', data);
  return response?.data || response;
};

/** Müşteri portalı fetch'leri: token hem localStorage hem modül (`authToken`) ile uyumlu olsun (getHeaders ile aynı mantık). */
const resolveClientJwtToken = (): string => {
  const pick = (v: unknown): string => {
    const s = String(v ?? '').trim();
    if (!s || s === 'undefined' || s === 'null') return '';
    return s;
  };

  if (typeof window === 'undefined') {
    return pick(authToken);
  }

  let effectiveToken: string | null = authToken;
  const isFieldApp =
    window.location.pathname.includes('/field') || window.location.pathname.includes('/mobile');
  if (isFieldApp) {
    effectiveToken = localStorage.getItem('fieldToken');
  } else if (!effectiveToken) {
    effectiveToken = localStorage.getItem('fieldToken');
  }

  const fromLs =
    pick(localStorage.getItem('authToken')) ||
    pick(localStorage.getItem('token')) ||
    pick(localStorage.getItem('clientToken'));

  const fromEffective = pick(effectiveToken);

  return fromLs || fromEffective;
};

// Helper function for client requests with workplace ID
const clientRequest = async (endpoint: string, workplaceId?: number, method: string = 'GET', body?: any) => {
  const token = resolveClientJwtToken();
  if (!token) {
    throw new Error(
      'Oturum bilgisi bulunamadı. Lütfen çıkış yapıp yeniden giriş yapın veya tarayıcıda üçüncü taraf çerez/depolama engellemesini kontrol edin.'
    );
  }

  const headers: Record<string, string> = {};
  const sendJsonBody = body !== undefined && method !== 'GET' && method !== 'DELETE';
  if (sendJsonBody) {
    headers['Content-Type'] = 'application/json';
  }
  headers['Authorization'] = `Bearer ${token}`;
  if (workplaceId) headers['x-workplace-id'] = workplaceId.toString();

  const options: RequestInit = { method, headers };
  if (sendJsonBody) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`/api${endpoint}`, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Request failed');
  }

  if (method === 'DELETE') {
    const data = await response.json().catch(() => ({}));
    return data;
  }

  const data = await response.json();
  return data?.data !== undefined ? data.data : data;
};

// Client Portal Service
export const client = {
  getDashboard: async (workplaceId?: number) => {
    return clientRequest('/client/dashboard', workplaceId);
  },
  
  // NEW: Widget-based dashboard endpoints
  getDashboardWidgets: async (workplaceId?: number) => {
    return clientRequest('/client/dashboard/widgets', workplaceId);
  },
  getCriticalAlerts: async (workplaceId?: number) => {
    return clientRequest('/client/alerts/critical', workplaceId);
  },
  getRecentActivities: async (workplaceId?: number) => {
    return clientRequest('/client/activities/recent', workplaceId);
  },
  getUpcomingEvents: async (workplaceId?: number) => {
    return clientRequest('/client/events/upcoming', workplaceId);
  },
  
  getDocuments: async (workplaceId?: number) => {
    return clientRequest('/client/documents', workplaceId);
  },
  /** Arşiv ile aynı klasör ağacı (Windows tarzı); x-workplace-id zorunlu */
  getWorkplaceFolderTree: async (workplaceId: number) => {
    return clientRequest(`/folders/workplace/${workplaceId}`, workplaceId);
  },
  getWorkplaceFolderRoot: async (workplaceId: number) => {
    return clientRequest(`/folders/workplace/${workplaceId}/root`, workplaceId);
  },
  getFolderExplorerContent: async (folderId: number, workplaceId: number) => {
    return clientRequest(`/folders/${folderId}`, workplaceId);
  },
  getTeam: async (workplaceId?: number) => {
    return clientRequest('/client/team', workplaceId);
  },
  getEmployees: async (workplaceId?: number) => {
    return clientRequest('/client/employees', workplaceId);
  },
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiRequest('/client/change-password', 'POST', {
      currentPassword,
      newPassword
    });
    return response?.data || response;
  },
  getActivities: async (workplaceId?: number) => {
    return clientRequest('/client/activities', workplaceId);
  },
  getActionItems: async (workplaceId: number) => {
    return clientRequest(`/client/workplace/${workplaceId}/action-items`, workplaceId);
  },
  getStatistics: async (workplaceId: number, year?: number) => {
    return clientRequest(`/client/workplace/${workplaceId}/statistics${year ? `?year=${year}` : ''}`, workplaceId);
  },
  getEmployeesTrackingSummary: async (workplaceId?: number) => {
    return clientRequest('/client/employees/tracking-summary', workplaceId);
  },
  /** Müşteri: Personel Excel şablonunu indirir (.xlsx) */
  downloadEmployeeTemplate: async (workplaceId?: number) => {
    const token = resolveClientJwtToken();
    if (!token) {
      throw new Error(
        'Oturum bilgisi bulunamadı. Lütfen çıkış yapıp yeniden giriş yapın veya tarayıcıda üçüncü taraf çerez/depolama engellemesini kontrol edin.'
      );
    }
    const headers: Record<string, string> = {};
    headers['Authorization'] = `Bearer ${token}`;
    const wpId = workplaceId || getActiveWorkplaceId();
    if (wpId) headers['x-workplace-id'] = String(wpId);
    const orgId = localStorage.getItem('organizationId');
    if (orgId) headers['x-organization-id'] = orgId;

    const res = await fetch(`${API_BASE_URL}/client/employees/template`, { headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as any).message || 'Şablon indirilemedi');
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition');
    let filename = 'Personel_Import_Sablonu.xlsx';
    const m = disposition?.match(/filename\*?=(?:UTF-8'')?([^;]+)/i);
    if (m) filename = decodeURIComponent(m[1].replace(/['"]/g, '').trim());
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  /** Müşteri: Excel ile toplu personel yüklemesi */
  importEmployeesFromExcel: async (file: File, workplaceId?: number) => {
    const token = resolveClientJwtToken();
    if (!token) {
      throw new Error(
        'Oturum bilgisi bulunamadı. Lütfen çıkış yapıp yeniden giriş yapın veya tarayıcıda üçüncü taraf çerez/depolama engellemesini kontrol edin.'
      );
    }
    const headers: Record<string, string> = {};
    headers['Authorization'] = `Bearer ${token}`;
    const wpId = workplaceId || getActiveWorkplaceId();
    if (wpId) headers['x-workplace-id'] = String(wpId);
    const orgId = localStorage.getItem('organizationId');
    if (orgId) headers['x-organization-id'] = orgId;

    const wpQuery = wpId ? `?workplaceId=${encodeURIComponent(String(wpId))}` : '';
    const formData = new FormData();
    formData.append('file', file);
    if (wpId) formData.append('workplaceId', String(wpId));

    const response = await fetch(`${API_BASE_URL}/client/employees/import${wpQuery}`, { method: 'POST', headers, body: formData });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error((body as any).message || 'Yükleme başarısız');
    return body as {
      success: boolean;
      message?: string;
      data?: {
        total: number;
        created: number;
        updated?: number;
        skipped?: number;
        skippedDuplicates?: number;
        errors: Array<{ row: number; message: string }>;
      };
    };
  },
  getEmployee: async (employeeId: number, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}`, workplaceId);
  },
  createEmployee: async (data: any, workplaceId?: number) => {
    return clientRequest('/client/employees', workplaceId, 'POST', data);
  },
  updateEmployee: async (employeeId: number, data: any, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}`, workplaceId, 'PUT', data);
  },
  deactivateEmployee: async (employeeId: number, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}`, workplaceId, 'DELETE');
  },
  activateEmployee: async (employeeId: number, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}/activate`, workplaceId, 'PUT');
  },
  uploadEmployeePhoto: async (employeeId: number, file: File, workplaceId?: number): Promise<any> => {
    const token = resolveClientJwtToken();
    if (!token) {
      throw new Error(
        'Oturum bilgisi bulunamadı. Lütfen çıkış yapıp yeniden giriş yapın veya tarayıcıda üçüncü taraf çerez/depolama engellemesini kontrol edin.'
      );
    }
    const headers: Record<string, string> = {};
    headers['Authorization'] = `Bearer ${token}`;
    const orgId = localStorage.getItem('organizationId');
    if (orgId) headers['x-organization-id'] = orgId;
    if (workplaceId) headers['x-workplace-id'] = String(workplaceId);
    const formData = new FormData();
    formData.append('photo', file);
    const response = await fetch(`/api/client/employees/${employeeId}/photo`, { method: 'POST', headers, body: formData });
    if (!response.ok) throw new Error('Fotoğraf yüklenemedi');
    return response.json();
  },
  getEmployeeTrainings: async (employeeId: number, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}/trainings`, workplaceId);
  },
  addEmployeeTraining: async (employeeId: number, data: any, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}/trainings`, workplaceId, 'POST', data);
  },
  deleteEmployeeTraining: async (employeeId: number, trainingId: number, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}/trainings/${trainingId}`, workplaceId, 'DELETE');
  },
  getEmployeeKKDList: async (employeeId: number, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}/kkd`, workplaceId);
  },
  addEmployeeKKDItem: async (employeeId: number, data: any, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}/kkd`, workplaceId, 'POST', data);
  },
  updateEmployeeHealth: async (employeeId: number, data: any, workplaceId?: number) => {
    return clientRequest(`/client/employees/${employeeId}/update-tracking`, workplaceId, 'PUT', data);
  }
};

// Organization Settings
export const organization = {
  getSettings: async () => {
    const response = await apiRequest('/organization/settings', 'GET');
    return response;
  },
  addBranch: async (branchData: { city: string; cityCode: string; personnelQuotas?: any[] }) => {
    const response = await apiRequest('/organization/branches', 'POST', branchData);
    clearCache('organization_settings');
    return response;
  },
  removeBranch: async (cityCode: string) => {
    const response = await apiRequest(`/organization/branches/${cityCode}`, 'DELETE');
    clearCache('organization_settings');
    return response;
  },
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiRequest('/organization/change-password', 'POST', {
      currentPassword,
      newPassword
    });
    return response?.data || response;
  },
  // User management
  getUsers: async () => {
    const response = await apiRequest('/organization/users', 'GET');
    return response?.data || response;
  },
  createUser: async (userData: { fullName: string; username: string; role: string; workplaceId?: number | null; branchCodes?: string[] | null }) => {
    const response = await apiRequest('/organization/users', 'POST', userData);
    return response?.data || response;
  },
  updateUser: async (id: number, userData: { fullName?: string; role?: string; workplaceId?: number | null; branchCodes?: string[] | null }) => {
    const response = await apiRequest(`/organization/users/${id}`, 'PUT', userData);
    return response?.data || response;
  },
  deleteUser: async (id: number) => {
    const response = await apiRequest(`/organization/users/${id}`, 'DELETE');
    return response;
  },
  resetUserPassword: async (id: number) => {
    const response = await apiRequest(`/organization/users/${id}/reset-password`, 'POST');
    return response?.data || response;
  },
  getUserPassword: async (id: number) => {
    const response = await apiRequest(`/organization/users/${id}/password`, 'GET');
    return response?.data || response;
  }
};

// Archive Management
export const archive = {
  getAllDocuments: async (filters?: {
    workplaceId?: number;
    documentType?: string | string[];
    startDate?: string;
    endDate?: string;
    status?: string;
    uploaderType?: string;
    uploadedBy?: number;
    isArchived?: boolean;
    search?: string;
    branchCity?: string;
    includeArchivedWorkplaces?: boolean | string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }
    const response = await apiRequest(`/archive?${params.toString()}`, 'GET');
    return response;
  },

  getStats: async (filters?: { branchCity?: string; includeArchivedWorkplaces?: boolean | string }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/archive/stats${queryString}`, 'GET');
  },

  getWorkplaceLookups: async (filters?: { branchCity?: string; includeArchived?: boolean | string }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/archive/workplaces-lookup${queryString}`, 'GET');
  },

  bulkDownload: async (documentIds: number[]) => {
    // For download, we need to handle blob response
    const response = await fetch(`${API_BASE_URL}/archive/bulk-download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ documentIds })
    });

    if (!response.ok) {
      throw new Error('Bulk download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `documents_${Date.now()}.zip`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  bulkDelete: async (documentIds: number[]) => {
    const response = await apiRequest('/archive/bulk-delete', 'DELETE', { documentIds });
    clearCache('archive');
    return response;
  },

  bulkArchive: async (documentIds: number[], isArchived: boolean) => {
    const response = await apiRequest('/archive/bulk-archive', 'PUT', { documentIds, isArchived });
    clearCache('archive');
    return response;
  }
};

export const folders = {
  getWorkplaceFolders: async (workplaceId: number) => {
    return await apiRequest(`/folders/workplace/${workplaceId}`, 'GET');
  },
  getRootContent: async (workplaceId: number) => {
    return await apiRequest(`/folders/workplace/${workplaceId}/root`, 'GET');
  },
  create: async (data: { name: string; parentId?: number | null; workplaceId: number }) => {
    return await apiRequest('/folders', 'POST', data);
  },
  getContent: async (folderId: number) => {
    return await apiRequest(`/folders/${folderId}`, 'GET');
  }
};

export const getAuthToken = () => authToken;

// Accounting Analysis API
export const uploadAccountingData = async (file: File, date: string) => {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('date', date);

  let uploadUrl = `${API_BASE_URL}/isg-katip/accounting-upload`;
  const selectedBranch = localStorage.getItem('selectedBranch');
  if (selectedBranch && selectedBranch !== 'Tüm Şubeler') {
    uploadUrl += `?branchCity=${encodeURIComponent(selectedBranch)}`;
  }

  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Upload failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Accounting upload error:', error);
    throw error;
  }
};

export const getAccountingStats = async (workplaceId: number, month: number, year: number, startDate?: string, endDate?: string) => {
  try {
    let url = `/isg-katip/accounting-stats/${workplaceId}?month=${month}&year=${year}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    return await apiRequest(url, 'GET');
  } catch (error) {
    console.error('Error fetching accounting stats:', error);
    throw error;
  }
};

export const getAccountingYearlyStats = async (workplaceId: number, year: number) => {
  try {
    return await apiRequest(`/isg-katip/accounting-stats/${workplaceId}/yearly?year=${year}`, 'GET');
  } catch (error) {
    console.error('Error fetching accounting yearly stats:', error);
    throw error;
  }
};

export const analyzeFromDb = async (date: string) => {
  try {
    return await apiRequest('/isg-katip/analyze-from-db', 'POST', { date });
  } catch (error) {
    console.error('Error analyzing from DB:', error);
    throw error;
  }
};

/** Ön muhasebe (cari / fatura / tahsilat) — Ultra hızlı şube bazlı işyeri listesi (<50ms) */
export const getAccountingWorkplaces = async (branchCity?: string, includeArchived?: boolean) => {
  const params = new URLSearchParams();
  if (branchCity && branchCity !== 'Tüm Şubeler' && branchCity !== 'all') {
    params.set('branchCity', branchCity);
  }
  if (includeArchived) {
    params.set('includeArchived', 'true');
  }
  const qs = params.toString();
  return await apiRequest(`/accounting/workplaces${qs ? `?${qs}` : ''}`, 'GET', undefined, { skipBranchFilter: true });
};

/** Ön muhasebe (cari / fatura / tahsilat) — skipBranchFilter: işyeri zaten seçili */
export const getPreAccountingCommercialSummary = async (workplaceId: number) => {
  return await apiRequest(`/accounting/workplace/${workplaceId}/commercial-summary`, 'GET', undefined, { skipBranchFilter: true });
};

export const patchPreAccountingCommercialFields = async (workplaceId: number, body: Record<string, unknown>) => {
  return await apiRequest(`/accounting/workplace/${workplaceId}/commercial-fields`, 'PATCH', body, { skipBranchFilter: true });
};

export const postPreAccountingInvoice = async (body: Record<string, unknown>) => {
  return await apiRequest('/accounting/invoices', 'POST', body, { skipBranchFilter: true });
};

export const postPreAccountingOpeningBalance = async (body: Record<string, unknown>) => {
  return await apiRequest('/accounting/opening-balance', 'POST', body, { skipBranchFilter: true });
};

export const postPreAccountingPayment = async (body: Record<string, unknown>) => {
  return await apiRequest('/accounting/payments', 'POST', body, { skipBranchFilter: true });
};

export const deletePreAccountingMovement = async (movementId: number) => {
  return await apiRequest(`/accounting/movements/${movementId}`, 'DELETE', undefined, { skipBranchFilter: true });
};

export const getPreAccountingStatement = async (workplaceId: number) => {
  return await apiRequest(`/accounting/statement/${workplaceId}`, 'GET', undefined, { skipBranchFilter: true });
};

export const getPreAccountingBalancesSummary = async () => {
  return await apiRequest('/accounting/balances-summary', 'GET', undefined, { skipBranchFilter: true });
};

export const getPreAccountingAnalyticsReceivableRange = (params: { min?: string | number; max?: string | number }) => {
  const q = new URLSearchParams();
  if (params.min !== undefined && params.min !== '') q.set('min', String(params.min));
  if (params.max !== undefined && params.max !== '') q.set('max', String(params.max));
  const qs = q.toString();
  return apiRequest(`/accounting/analytics/by-receivable-range${qs ? `?${qs}` : ''}`, 'GET', undefined, { skipBranchFilter: true });
};

export const getPreAccountingAnalyticsNoPaymentInRange = (from: string, to: string) =>
  apiRequest(
    `/accounting/analytics/no-payment-in-range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    'GET',
    undefined,
    { skipBranchFilter: true }
  );

export const getPreAccountingAnalyticsByTrackingExpert = (expertId: number) =>
  apiRequest(`/accounting/analytics/by-tracking-expert?expertId=${expertId}`, 'GET', undefined, { skipBranchFilter: true });

export const getPreAccountingAnalyticsByRisk = (riskLevel: string) =>
  apiRequest(`/accounting/analytics/by-risk?riskLevel=${encodeURIComponent(riskLevel)}`, 'GET', undefined, { skipBranchFilter: true });

export const getPreAccountingAnalyticsReceivablePeriod = (from: string, to: string) =>
  apiRequest(
    `/accounting/analytics/receivable-period?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    'GET',
    undefined,
    { skipBranchFilter: true }
  );

export const getPreAccountingAnalyticsTopReceivable = (limit?: number) =>
  apiRequest(`/accounting/analytics/top-receivable${limit ? `?limit=${limit}` : ''}`, 'GET', undefined, { skipBranchFilter: true });

export const getPreAccountingAnalyticsStaleReceivable = (minDays?: number) =>
  apiRequest(`/accounting/analytics/stale-receivable${minDays ? `?minDays=${minDays}` : ''}`, 'GET', undefined, { skipBranchFilter: true });

const preAccountingBlobUrl = (path: string) => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const orgId = localStorage.getItem('organizationId');
  if (orgId) headers['x-organization-id'] = orgId;
  let url = `${API_BASE_URL}${path}`;
  const selectedBranch = localStorage.getItem('selectedBranch');
  if (selectedBranch && selectedBranch !== 'Tüm Şubeler') {
    url += `${path.includes('?') ? '&' : '?'}branchCity=${encodeURIComponent(selectedBranch)}`;
  }
  return { url, headers };
};

export const downloadPreAccountingExcel = async (workplaceId?: number, from?: string, to?: string) => {
  let q = workplaceId != null ? `?workplaceId=${workplaceId}` : '?';
  if (from) q += `${q.includes('?') && q.length > 1 ? '&' : ''}from=${encodeURIComponent(from)}`;
  if (to)   q += `&to=${encodeURIComponent(to)}`;
  const { url, headers } = preAccountingBlobUrl(`/accounting/export/excel${q}`);
  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) throw new Error('Excel indirilemedi');
  return response.blob();
};

export const downloadPreAccountingPdf = async (workplaceId: number, from?: string, to?: string) => {
  let q = `?workplaceId=${workplaceId}`;
  if (from) q += `&from=${encodeURIComponent(from)}`;
  if (to)   q += `&to=${encodeURIComponent(to)}`;
  const { url, headers } = preAccountingBlobUrl(`/accounting/export/pdf${q}`);
  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) throw new Error('PDF indirilemedi');
  return response.blob();
};

export const postPreAccountingInvoiceImportPreview = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const orgId = localStorage.getItem('organizationId');
  if (orgId) headers['x-organization-id'] = orgId;

  const url = `${API_BASE_URL}/accounting/invoices/import-preview`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Fatura dosyası yüklenirken bir hata oluştu');
  }
  return data;
};

export const postPreAccountingInvoiceImportConfirm = async (invoices: any[]) => {
  return await apiRequest('/accounting/invoices/import-confirm', 'POST', { invoices }, { skipBranchFilter: true });
};

// Assignment History API
export const getAssignmentHistory = async (params: any) => {
    const query = new URLSearchParams(params).toString();
    return await apiRequest('/assignment-history?' + query, 'GET');
};

export const exportAssignmentHistory = async (params: any) => {
    try {
        const token = localStorage.getItem('authToken');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const orgId = localStorage.getItem('organizationId');
        if (orgId) headers['x-organization-id'] = orgId;

        const query = new URLSearchParams(params).toString();
        let url = `${API_BASE_URL}/assignment-history/export?${query}`;

        const response = await fetch(url, {
            method: 'GET',
            headers
        });

        if (!response.ok) {
            throw new Error(`Export failed: ${response.statusText}`);
        }

        const blob = await response.blob();
        return blob;
    } catch (error) {
        logger.error('Error exporting assignment history:', error);
        throw error;
    }
};

export const getAssignmentHistoryStats = async (params: any = {}) => {
    const query = new URLSearchParams(params).toString();
    const endpoint = query ? `/assignment-history/stats?${query}` : '/assignment-history/stats';
    return await apiRequest(endpoint, 'GET');
};

